import * as LDClient from 'launchdarkly-js-client-sdk';
import { config } from '../config';
import { type FlagSet, type FlagKey, flagDefaults } from './types';

type Listener = (flags: FlagSet, changed: FlagKey[]) => void;

let client: LDClient.LDClient | null = null;
let current: FlagSet = { ...flagDefaults };
const listeners = new Set<Listener>();

export async function initFlags(): Promise<FlagSet> {
  if (!config.ldClientId) {
    console.warn('[flags] No LaunchDarkly client ID configured; using defaults.');
    return current;
  }

  client = LDClient.initialize(
    config.ldClientId,
    { kind: 'user', key: getOrCreateAnonKey(), anonymous: true }
  );

  try {
    await client.waitForInitialization(5);
    current = readAll();
    console.info('[flags] LaunchDarkly initialized', current);
  } catch (err) {
    console.warn('[flags] LaunchDarkly init failed; continuing with defaults.', err);
    return current;
  }

  client.on('change', (settings: Record<string, { current: unknown }>) => {
    const changed = Object.keys(settings) as FlagKey[];
    current = readAll();
    listeners.forEach((fn) => fn(current, changed));
  });

  return current;
}

export function getFlags(): FlagSet {
  return current;
}

export function onFlagsChange(fn: Listener): () => void {
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
}

function readAll(): FlagSet {
  if (!client) return { ...flagDefaults };
  const out = { ...flagDefaults };
  (Object.keys(flagDefaults) as FlagKey[]).forEach((key) => {
    const value = client!.variation(key, flagDefaults[key]);
    (out as Record<string, unknown>)[key] = value;
  });
  return out;
}

function getOrCreateAnonKey(): string {
  const STORAGE_KEY = 'momentum.demo.anon';
  let value = localStorage.getItem(STORAGE_KEY);
  if (!value) {
    value = `anon-${randomId()}`;
    localStorage.setItem(STORAGE_KEY, value);
  }
  return value;
}

// crypto.randomUUID is only available in secure contexts, so it throws when
// hitting the dev server from a phone via http://<lan-ip>. Fall back to
// getRandomValues, then Math.random — the key only needs to be unique per
// browser, not cryptographically strong.
function randomId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
    const bytes = new Uint8Array(16);
    crypto.getRandomValues(bytes);
    return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
  }
  return `${Date.now().toString(16)}-${Math.random().toString(16).slice(2)}`;
}
